/**
 * MH-36 Danh mục hành chính ba cấp + ba trợ lý tổ chức lại (đổi tên, sáp nhập,
 * chia tách) — đợt 3 phần C4, yêu cầu nghiệp vụ mục 3 (phần khó nhất).
 *
 * Cả ba trợ lý bắt buộc hai bước: XEM TRƯỚC ảnh hưởng (số thiết bị/bản tin/lịch
 * phát) rồi mới chọn cách xử lý dữ liệu lịch sử và XÁC NHẬN thực hiện — không
 * gộp làm một bước. Sửa trực tiếp mã định danh của đơn vị đang có thiết bị bị
 * máy chủ chặn (`OPERATION_NOT_ALLOWED`); giao diện hiển thị đúng thông báo đó,
 * không tự đoán ý định thay bằng lối tắt khác.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { dienVao, vi } from '../ngon-ngu/vi';
import { danhMucApi, type AnhHuong, type DongDonVi, type XuLyLichSu } from '../api/danh-muc-hanh-chinh-api';
import { LoiApi } from '../api/goi-chung';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { KhungXuong, TrangThaiLoi } from '../thanh-phan/TrangThaiManHinh';
import { HopNhapDonVi } from './HopNhapDonVi';

interface NutCay extends DongDonVi {
  con: NutCay[];
}

function dungCay(ds: DongDonVi[]): NutCay[] {
  const theoMa = new Map<string, NutCay>(ds.map((d) => [d.maDinhDanh, { ...d, con: [] }]));
  const goc: NutCay[] = [];
  for (const n of theoMa.values()) {
    if (n.maCha && theoMa.has(n.maCha)) theoMa.get(n.maCha)!.con.push(n);
    else goc.push(n);
  }
  return goc;
}

function CayNode({
  n,
  daChon,
  onChon,
}: {
  n: NutCay;
  daChon: Set<string>;
  onChon: (n: NutCay) => void;
}): ReactNode {
  return (
    <li>
      <label>
        <input type="checkbox" checked={daChon.has(n.id)} onChange={() => onChon(n)} />
        {n.ten} <code>{n.maDinhDanh}</code> ({vi.danhMucHanhChinh.cap[n.cap]})
      </label>
      {n.con.length > 0 ? (
        <ul role="list">
          {n.con.map((c) => (
            <CayNode key={c.id} n={c} daChon={daChon} onChon={onChon} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

type TenTroLy = null | 'doi-ten' | 'sap-nhap' | 'chia-tach';

export function MH36DanhMucHanhChinh(): ReactNode {
  const [moc, datMoc] = useState(0);
  const [troLy, datTroLy] = useState<TenTroLy>(null);
  const [daChon, datDaChon] = useState<Set<string>>(new Set());
  const [tenMoi, datTenMoi] = useState('');
  const [maMoi, datMaMoi] = useState('');
  const [xuLyLichSu, datXuLyLichSu] = useState<XuLyLichSu>('giu_nguyen');
  const [donViMoiChiaTach, datDonViMoiChiaTach] = useState<{ maMoi: string; tenMoi: string }[]>([
    { maMoi: '', tenMoi: '' },
    { maMoi: '', tenMoi: '' },
  ]);
  const [xemTruoc, datXemTruoc] = useState<({ apDung: boolean } & AnhHuong) | null>(null);
  const [dangGui, datDangGui] = useState(false);
  const [loiGui, datLoiGui] = useState<string | null>(null);
  const [thongBao, datThongBao] = useState<string | null>(null);
  const [moNhap, datMoNhap] = useState(false);

  const kq = dungTaiDuLieu<DongDonVi[]>({
    tai: (signal) => danhMucApi.cay(signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [moc],
  });

  const taiLai = (): void => datMoc((x) => x + 1);
  const cay = useMemo(() => dungCay(kq.duLieu ?? []), [kq.duLieu]);
  const theoId = useMemo(() => new Map((kq.duLieu ?? []).map((d) => [d.id, d])), [kq.duLieu]);

  const batDauTroLy = (ten: TenTroLy): void => {
    datTroLy(ten);
    datXemTruoc(null);
    datLoiGui(null);
    datTenMoi('');
    datMaMoi('');
  };

  const chonDong = (n: NutCay): void => {
    datXemTruoc(null);
    datDaChon((truoc) => {
      const moi = new Set(troLy === 'sap-nhap' ? truoc : []);
      if (moi.has(n.id)) moi.delete(n.id);
      else moi.add(n.id);
      return moi;
    });
  };

  const donViDauChon = daChon.size > 0 ? theoId.get([...daChon][0]!) : undefined;

  const guiDoiTenXemTruoc = async (): Promise<void> => {
    if (!donViDauChon) return;
    datDangGui(true);
    datLoiGui(null);
    try {
      const r = await danhMucApi.doiTen(donViDauChon.id, { tenMoi });
      datXemTruoc(r);
    } catch (err) {
      datLoiGui(err instanceof LoiApi ? err.message : 'Không xem trước được');
    } finally {
      datDangGui(false);
    }
  };

  const guiDoiTenApDung = async (): Promise<void> => {
    if (!donViDauChon) return;
    datDangGui(true);
    datLoiGui(null);
    try {
      await danhMucApi.doiTen(donViDauChon.id, { tenMoi, xacNhan: true });
      datThongBao(vi.danhMucHanhChinh.daThucHien);
      batDauTroLy(null);
      taiLai();
    } catch (err) {
      datLoiGui(err instanceof LoiApi ? err.message : 'Không thực hiện được');
    } finally {
      datDangGui(false);
    }
  };

  const guiSapNhapXemTruoc = async (): Promise<void> => {
    datDangGui(true);
    datLoiGui(null);
    try {
      const r = await danhMucApi.sapNhap({ nguonIds: [...daChon], maMoi, tenMoi, xuLyLichSu });
      datXemTruoc(r);
    } catch (err) {
      datLoiGui(err instanceof LoiApi ? err.message : 'Không xem trước được');
    } finally {
      datDangGui(false);
    }
  };

  const guiSapNhapApDung = async (): Promise<void> => {
    datDangGui(true);
    datLoiGui(null);
    try {
      await danhMucApi.sapNhap({ nguonIds: [...daChon], maMoi, tenMoi, xuLyLichSu, xacNhan: true });
      datThongBao(vi.danhMucHanhChinh.daThucHien);
      batDauTroLy(null);
      datDaChon(new Set());
      taiLai();
    } catch (err) {
      datLoiGui(err instanceof LoiApi ? err.message : 'Không thực hiện được');
    } finally {
      datDangGui(false);
    }
  };

  const guiChiaTachXemTruoc = async (): Promise<void> => {
    if (!donViDauChon) return;
    datDangGui(true);
    datLoiGui(null);
    try {
      const r = await danhMucApi.chiaTach({
        nguonId: donViDauChon.id,
        donViMoi: donViMoiChiaTach,
        phanCong: [],
        xuLyLichSu,
      });
      datXemTruoc(r);
    } catch (err) {
      datLoiGui(err instanceof LoiApi ? err.message : 'Không xem trước được');
    } finally {
      datDangGui(false);
    }
  };

  const guiChiaTachApDung = async (): Promise<void> => {
    if (!donViDauChon) return;
    datDangGui(true);
    datLoiGui(null);
    try {
      const conTrucTiep = (kq.duLieu ?? []).filter((d) => d.maCha === donViDauChon.maDinhDanh);
      const canPhanCong =
        conTrucTiep.length > 0 ? conTrucTiep.map((c) => c.maDinhDanh) : [donViDauChon.maDinhDanh];
      // Gán tuần tự đơn vị mới cho từng đơn vị cần phân công (đơn giản hoá giao
      // diện: người dùng chọn số đơn vị mới đúng bằng số đơn vị cần gán khi cần
      // phân công cụ thể; trường hợp đơn giản 1→N không con thì gán vào cái đầu).
      const phanCong = canPhanCong.map((maCu, i) => ({
        maCu,
        maMoiChiDinh: donViMoiChiaTach[i % donViMoiChiaTach.length]!.maMoi,
      }));
      await danhMucApi.chiaTach({
        nguonId: donViDauChon.id,
        donViMoi: donViMoiChiaTach,
        phanCong,
        xuLyLichSu,
        xacNhan: true,
      });
      datThongBao(vi.danhMucHanhChinh.daThucHien);
      batDauTroLy(null);
      datDaChon(new Set());
      taiLai();
    } catch (err) {
      datLoiGui(err instanceof LoiApi ? err.message : 'Không thực hiện được');
    } finally {
      datDangGui(false);
    }
  };

  if (kq.trangThai === 'dang-tai') return <KhungXuong soCot={4} />;
  if (kq.trangThai === 'loi' && kq.loi) return <TrangThaiLoi loi={kq.loi} onThuLai={taiLai} />;

  return (
    <div className="man-hinh man-hinh--danh-muc-hanh-chinh">
      <header className="man-hinh__dau">
        <h1>{vi.danhMucHanhChinh.tieuDe}</h1>
        <div className="man-hinh__hanh-dong">
          <button
            type="button"
            className="nut nut--chinh"
            data-hanh-dong="nhap-tu-tep"
            onClick={() => datMoNhap(true)}
          >
            {vi.danhMucHanhChinh.nhapTuTep}
          </button>
          <button
            type="button"
            className="nut"
            onClick={() => batDauTroLy('doi-ten')}
            disabled={daChon.size !== 1}
          >
            {vi.danhMucHanhChinh.troLyDoiTen}
          </button>
          <button
            type="button"
            className="nut"
            onClick={() => batDauTroLy('sap-nhap')}
            disabled={daChon.size < 2}
          >
            {vi.danhMucHanhChinh.troLySapNhap}
          </button>
          <button
            type="button"
            className="nut"
            onClick={() => batDauTroLy('chia-tach')}
            disabled={daChon.size !== 1}
          >
            {vi.danhMucHanhChinh.troLyChiaTach}
          </button>
        </div>
      </header>

      {thongBao ? <p role="status">{thongBao}</p> : null}

      {cay.length === 0 ? (
        <div className="bang__rong" data-vung="danh-muc-rong">
          <p>
            <strong>{vi.danhMucHanhChinh.rongMoTa}</strong>
          </p>
          <p>{vi.danhMucHanhChinh.rongHuongDan}</p>
          <button type="button" className="nut nut--chinh" onClick={() => datMoNhap(true)}>
            {vi.danhMucHanhChinh.nhapTuTep}
          </button>
        </div>
      ) : null}

      {moNhap ? (
        <HopNhapDonVi
          onDong={() => datMoNhap(false)}
          onDaNhap={(so) => {
            datMoNhap(false);
            datThongBao(dienVao(vi.danhMucHanhChinh.daNhap, { so }));
            taiLai();
          }}
        />
      ) : null}

      <ul role="list" data-vung="cay-hanh-chinh">
        {cay.map((n) => (
          <CayNode key={n.id} n={n} daChon={daChon} onChon={chonDong} />
        ))}
      </ul>

      {troLy ? (
        <div className="hop-thoai" data-vung={`tro-ly-${troLy}`}>
          <h2>
            {troLy === 'doi-ten'
              ? vi.danhMucHanhChinh.troLyDoiTen
              : troLy === 'sap-nhap'
                ? vi.danhMucHanhChinh.troLySapNhap
                : vi.danhMucHanhChinh.troLyChiaTach}
          </h2>

          {troLy === 'doi-ten' ? (
            <label>
              {vi.danhMucHanhChinh.tenMoi}
              <input value={tenMoi} onChange={(e) => datTenMoi(e.target.value)} required />
            </label>
          ) : null}

          {troLy === 'sap-nhap' ? (
            <>
              <label>
                {vi.danhMucHanhChinh.maMoi}
                <input value={maMoi} onChange={(e) => datMaMoi(e.target.value)} required />
              </label>
              <label>
                {vi.danhMucHanhChinh.tenMoi}
                <input value={tenMoi} onChange={(e) => datTenMoi(e.target.value)} required />
              </label>
            </>
          ) : null}

          {troLy === 'chia-tach' ? (
            <div data-vung="don-vi-moi-chia-tach">
              {donViMoiChiaTach.map((dv, i) => (
                <div key={i} className="man-hinh__hanh-dong">
                  <input
                    aria-label={vi.danhMucHanhChinh.maMoi}
                    placeholder={vi.danhMucHanhChinh.maMoi}
                    value={dv.maMoi}
                    onChange={(e) =>
                      datDonViMoiChiaTach((ds) =>
                        ds.map((x, j) => (j === i ? { ...x, maMoi: e.target.value } : x)),
                      )
                    }
                  />
                  <input
                    aria-label={vi.danhMucHanhChinh.tenMoi}
                    placeholder={vi.danhMucHanhChinh.tenMoi}
                    value={dv.tenMoi}
                    onChange={(e) =>
                      datDonViMoiChiaTach((ds) =>
                        ds.map((x, j) => (j === i ? { ...x, tenMoi: e.target.value } : x)),
                      )
                    }
                  />
                </div>
              ))}
              <button
                type="button"
                className="nut nut--nho"
                onClick={() => datDonViMoiChiaTach((ds) => [...ds, { maMoi: '', tenMoi: '' }])}
              >
                +
              </button>
            </div>
          ) : null}

          {troLy !== 'doi-ten' ? (
            <fieldset>
              <legend>{vi.danhMucHanhChinh.xuLyLichSu}</legend>
              <label>
                <input
                  type="radio"
                  name="xuLyLichSu"
                  checked={xuLyLichSu === 'giu_nguyen'}
                  onChange={() => datXuLyLichSu('giu_nguyen')}
                />
                {vi.danhMucHanhChinh.xuLyGiuNguyen}
              </label>
              <label>
                <input
                  type="radio"
                  name="xuLyLichSu"
                  checked={xuLyLichSu === 'gan_lai'}
                  onChange={() => datXuLyLichSu('gan_lai')}
                />
                {vi.danhMucHanhChinh.xuLyGanLai}
              </label>
            </fieldset>
          ) : null}

          {loiGui ? <p role="alert">{loiGui}</p> : null}

          {xemTruoc ? (
            <div data-vung="xem-truoc-anh-huong">
              <h3>{vi.danhMucHanhChinh.anhHuongTieuDe}</h3>
              <p>
                {vi.danhMucHanhChinh.soThietBi}: <strong>{xemTruoc.soThietBi}</strong> ·{' '}
                {vi.danhMucHanhChinh.soBanTin}: <strong>{xemTruoc.soBanTin}</strong> ·{' '}
                {vi.danhMucHanhChinh.soLichPhat}: <strong>{xemTruoc.soLichPhat}</strong>
              </p>
              <button
                type="button"
                className="nut nut--chinh"
                disabled={dangGui}
                onClick={() =>
                  void (troLy === 'doi-ten'
                    ? guiDoiTenApDung()
                    : troLy === 'sap-nhap'
                      ? guiSapNhapApDung()
                      : guiChiaTachApDung())
                }
              >
                {vi.danhMucHanhChinh.xacNhanThucHien}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="nut nut--chinh"
              disabled={dangGui}
              onClick={() =>
                void (troLy === 'doi-ten'
                  ? guiDoiTenXemTruoc()
                  : troLy === 'sap-nhap'
                    ? guiSapNhapXemTruoc()
                    : guiChiaTachXemTruoc())
              }
            >
              {vi.danhMucHanhChinh.xemTruocAnhHuong}
            </button>
          )}
          <button type="button" className="nut" onClick={() => batDauTroLy(null)}>
            {vi.danhMucHanhChinh.huy}
          </button>
        </div>
      ) : null}
    </div>
  );
}
